import {
	forwardRef,
	memo,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from "react"
import { SortableContext, useSortable } from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { useNavigate, useParams } from "@tanstack/react-router"
import {
	IconCopyOutline18 as Copy,
	IconDotsOutline18 as Dots,
	IconMoveToRightOutline18 as MoveToPortal,
	IconPlusOutline18 as Plus,
	IconTrashOutline18 as Trash,
} from "nucleo-ui-outline-18"

import type {
	HelpCenterArticle,
	HelpCenterArticleTranslation,
} from "@productlane/zero"
import { generateId } from "@productlane/lib/src/browser"
import { cn } from "@productlane/lib/src/cn"
import {
	AlertDialog,
	IconButton,
	NucleoFillIcon,
	Tooltip,
} from "@productlane/ui"
import CustomChevron from "@productlane/ui/src/components/CustomChevron"
import {
	DropdownMenu,
	DropdownMenuItem,
} from "@productlane/ui/src/components/internal/dropdown"
import { mutators } from "@productlane/zero/mutators"

import { NucleoFillIconPicker } from "@/components/nucleo-fill-icon-picker"
import { RoleCheck } from "@/components/shared/role-check"
import { analytics } from "@/lib/analytics"
import { useRole } from "@/lib/useRole"
import { mutateOk, useZero, useZeroWorkspace } from "@/lib/zero"
import { useDocNavStore } from "@/stores/docs/docs-nav-store"
import { getArticleDisplayTitle } from "./article-kind"
import { ArticleStatePill } from "./article-state-pill"
import { getDefaultArticleIcon } from "./article-visibility"
import { useHelpCenterNav } from "./help-center-context"
import { useDropSignal } from "./help-center-drop-indicator"
import { MoveToPortalDialog } from "./move-to-portal-dialog"
import { NavRowActions } from "./nav-row-actions"
import { noSortShift } from "./no-sort-shift"
import { translatedTitle } from "./translated-title"
import { useDelayedUnmount } from "./use-delayed-unmount"

type SubpageWithTranslations = HelpCenterArticle & {
	translations?: readonly HelpCenterArticleTranslation[]
}

type HelpCenterArticleWithTranslations = HelpCenterArticle & {
	translations?: readonly HelpCenterArticleTranslation[]
	subpages?: readonly SubpageWithTranslations[]
}

export const HelpCenterNavItem = memo(
	forwardRef<
		HTMLLIElement,
		{
			article: HelpCenterArticleWithTranslations
			groupId: string
			sortableDisabled: boolean
		}
	>(({ article, groupId, sortableDisabled }, ref) => {
		const params = useParams({ strict: false })
		const { role } = useRole()
		const navigate = useNavigate()
		const zero = useZero()
		const isSelected = params.id === article.id
		const [isDeleteOpen, setIsDeleteOpen] = useState(false)
		const [isMoveOpen, setIsMoveOpen] = useState(false)
		const { onItemSelected, selectedLanguage } = useHelpCenterNav()
		const workspace = useZeroWorkspace()
		const hasOtherPortals = (workspace?.portalInstances ?? []).length >= 1

		const title = translatedTitle(article, selectedLanguage)
		const label = getArticleDisplayTitle(article, title)

		// A fresh object here re-keys dnd-kit's droppable data on every render,
		// which churns the whole SortableContext while dragging.
		const sortableData = useMemo(
			() => ({
				type: "article" as const,
				groupId,
				parentArticleId: article.parentArticleId ?? null,
				article,
			}),
			[groupId, article],
		)

		const {
			attributes,
			listeners,
			setNodeRef,
			transform,
			transition,
			isDragging,
		} = useSortable({
			id: article.id,
			disabled: sortableDisabled,
			data: sortableData,
		})

		const style = {
			transform: CSS.Translate.toString(transform),
			transition,
			opacity: isDragging ? 0.4 : undefined,
		}
		const dropSignal = useDropSignal(article.id)

		const handleDelete = useCallback(() => {
			setIsDeleteOpen(true)
			analytics.track("open-delete-docs-dialog", {
				id: article.id,
			})
		}, [article.id])

		const handleDuplicate = useCallback(async () => {
			const duplicateId = generateId()
			const result = await zero.mutate(
				mutators.helpCenterArticles.duplicate({
					id: article.id,
					duplicateId,
				}),
			).client
			if (result.type === "success") {
				analytics.track("helpcenterarticle-duplicate", {
					id: article.id,
					duplicateId,
				})
				void navigate({ to: "/edit-docs/$id", params: { id: duplicateId } })
			} else {
				console.error("Failed to duplicate article:", result.error)
			}
		}, [article.id, navigate, zero])

		const canHaveSubpages = !article.parentArticleId
		const subpages = article.subpages ?? []
		const subpageCount = subpages.length
		const hasSubpages = subpageCount > 0
		// Selectors, not the whole store: a bare useDocNavStore() re-renders every
		// row in the tree on any open/close anywhere in the nav.
		const isExpanded = useDocNavStore((s) =>
			s.openArticleIds.includes(article.id),
		)
		const wasManuallyClosed = useDocNavStore((s) =>
			s.manuallyClosedArticles.includes(article.id),
		)
		const openArticle = useDocNavStore((s) => s.openArticle)
		const toggleArticle = useDocNavStore((s) => s.toggleArticle)
		const isSubpageSelected = useMemo(
			() => subpages.some((s) => s.id === params.id),
			// eslint-disable-next-line react-hooks/exhaustive-deps
			[subpages, params.id],
		)
		// eslint-disable-next-line react-hooks/exhaustive-deps
		const subpageIds = useMemo(() => subpages.map((s) => s.id), [subpages])
		const shouldShowSubpages =
			hasSubpages && (isExpanded || (isSubpageSelected && !wasManuallyClosed))
		const renderSubpages = useDelayedUnmount(shouldShowSubpages, 200)

		useEffect(() => {
			if (isSubpageSelected && !isExpanded && !wasManuallyClosed) {
				openArticle(article.id)
			}
		}, [
			isSubpageSelected,
			isExpanded,
			wasManuallyClosed,
			article.id,
			openArticle,
		])

		const handleAddSubpage = useCallback(
			async (e: React.MouseEvent) => {
				e.preventDefault()
				e.stopPropagation()
				const id = generateId()
				await mutateOk(
					zero.mutate(
						mutators.helpCenterArticles.create({
							id,
							title: "",
							helpCenterGroupId: article.helpCenterGroupId,
							parentArticleId: article.id,
							order: 0,
							archived: false,
							content: {},
							date: Date.now(),
							icon: getDefaultArticleIcon(article.visibility),
							imageBlurhash: null,
							imageUrl: "",
							published: false,
							visibility: article.visibility,
							showOnHomePage: false,
							summary: "",
							urlName: "",
							// Subpage inherits the parent's portal scope so siblings stay together.
							portalInstanceId: article.portalInstanceId ?? null,
						}),
					),
				)
				analytics.track("helpcenterarticle-subpage-create", {
					parentId: article.id,
					id,
				})
				void navigate({ to: "/edit-docs/$id", params: { id } })
				onItemSelected?.()
			},
			// eslint-disable-next-line react-hooks/exhaustive-deps
			[article.helpCenterGroupId, article.id, navigate, onItemSelected, zero],
		)

		return (
			<>
				{isMoveOpen && (
					<MoveToPortalDialog
						isOpen={isMoveOpen}
						onClose={() => setIsMoveOpen(false)}
						target={{
							kind: "article",
							id: article.id,
							name: label || "Untitled",
							groupId: article.helpCenterGroupId,
							portalInstanceId: article.portalInstanceId ?? null,
						}}
					/>
				)}
				<AlertDialog
					isOpen={isDeleteOpen}
					onOpenChange={setIsDeleteOpen}
					confirmText="Delete"
					title={title ? `Delete "${title}"?` : "Delete this article?"}
					description={
						title
							? `Are you sure you want to delete the article "${title}"?`
							: "Are you sure you want to delete this article?"
					}
					onCancel={() => {
						analytics.track("cancel-delete-helpcenterarticle", {
							id: article.id,
						})
						setIsDeleteOpen(false)
					}}
					onConfirm={() => {
						zero.mutate(
							mutators.helpCenterArticles.delete({
								id: article.id,
							}),
						)
						analytics.track("helpcenterarticle-delete", { id: article.id })
						void navigate({ to: "/edit-docs" })
						setIsDeleteOpen(false)
					}}
				/>
				<li
					ref={(el) => {
						setNodeRef(el)
						if (typeof ref === "function") ref(el)
						else if (ref) ref.current = el
					}}
					style={style}
					{...attributes}
				>
					<div
						className={cn(
							"group/item relative flex w-full min-w-0 items-center justify-between rounded-md ring-inset transition-shadow",
							isSelected
								? "bg-gray-100 text-primary dark:bg-gray-800"
								: "bg-transparent hover:bg-gray-100 hover:dark:bg-gray-800/50",
							dropSignal === "nest" &&
								"bg-gray-100 ring-2 ring-black dark:bg-gray-800/60 dark:ring-white",
						)}
					>
						{dropSignal === "before" && (
							<div className="pointer-events-none absolute inset-x-1 -top-0.5 z-20 h-0.5 rounded-full bg-black dark:bg-white" />
						)}
						{dropSignal === "after" && (
							<div className="pointer-events-none absolute inset-x-1 -bottom-0.5 z-20 h-0.5 rounded-full bg-black dark:bg-white" />
						)}
						{dropSignal === "nest" && (
							<span className="pointer-events-none absolute -bottom-4 left-4 z-20 whitespace-nowrap rounded bg-black px-1.5 py-0.5 text-[0.65rem] font-medium text-white shadow-sm dark:bg-white dark:text-gray-950">
								Drop to nest inside
							</span>
						)}
						<div
							role="button"
							tabIndex={0}
							className={cn(
								"group/navlink flex h-[1.9rem] w-full min-w-0 cursor-default select-none items-center gap-2 rounded-md pl-2 pr-1 text-sm font-medium",
							)}
							{...listeners}
							onClick={() => {
								void navigate({
									to: "/edit-docs/$id",
									params: { id: article.id },
								})
								onItemSelected?.()
							}}
							onKeyDown={(e) => {
								if (e.key === "Enter" || e.key === " ") {
									e.preventDefault()
									void navigate({
										to: "/edit-docs/$id",
										params: { id: article.id },
									})
									onItemSelected?.()
								}
							}}
						>
							<div
								role="presentation"
								className="flex w-full min-w-0 flex-1 items-center gap-2.5"
								onClick={(e) => {
									if ((e.target as HTMLElement).closest("button"))
										e.stopPropagation()
								}}
								onKeyDown={(e) => {
									if ((e.target as HTMLElement).closest("button"))
										e.stopPropagation()
								}}
							>
								<NucleoFillIconPicker
									buttonVariant="link"
									icon={article.icon}
									disabled={role === "VIEWER"}
									className="h-[1rem] w-[1rem] text-secondary group-hover/navlink:text-primary"
									onChange={async (icon) => {
										if (!icon) return
										await mutateOk(
											zero.mutate(
												mutators.helpCenterArticles.update({
													id: article.id,
													icon,
												}),
											),
										)
										analytics.track("update-helpcenterarticleicon", {
											id: article.id,
										})
									}}
								/>
								<span
									title={label}
									className="min-h-3.5 min-w-0 flex-1 truncate text-ellipsis text-start leading-loose opacity-90"
								>
									{label}
								</span>
							</div>
							<ArticleStatePill
								article={article}
								className="pointer-events-none ml-1 flex-shrink-0 opacity-100"
							/>
							{hasSubpages && (
								<button
									type="button"
									aria-label={
										isExpanded ? "Collapse subpages" : "Expand subpages"
									}
									aria-expanded={isExpanded}
									title={`${subpageCount} subpage${subpageCount === 1 ? "" : "s"}`}
									onClick={(e) => {
										e.preventDefault()
										e.stopPropagation()
										toggleArticle(article.id)
									}}
									className="relative z-10 ml-1 flex h-5 flex-shrink-0 items-center gap-1 rounded-md px-1 text-[0.7rem] text-gray-500 hover:bg-gray-200 hover:text-primary dark:text-gray-400 dark:hover:bg-gray-700"
								>
									<span>{subpageCount > 99 ? "99+" : subpageCount}</span>
									<CustomChevron
										width={7}
										height={7}
										strokeWidth={2.25}
										className={cn(
											isExpanded ? "rotate-90" : "rotate-0",
											"text-tertiary transition-transform",
										)}
									/>
								</button>
							)}
						</div>
						<RoleCheck not={["VIEWER"]}>
							<NavRowActions
								isSelected={isSelected}
								className={hasSubpages ? "right-9" : "right-1"}
							>
								<DropdownMenu
									contentProps={{
										align: "end",
										className: "w-fit pr-1",
									}}
									trigger={
										<IconButton
											Icon={Dots}
											variant="ghost"
											size="md"
											iconStrokeWidth={1.75}
											className="relative hover:bg-gray-200 dark:hover:bg-gray-700"
											type="button"
										/>
									}
								>
									<DropdownMenuItem Icon={Copy} onClick={handleDuplicate}>
										Duplicate
									</DropdownMenuItem>
									{hasOtherPortals && (
										<DropdownMenuItem
											Icon={MoveToPortal}
											onClick={() => setIsMoveOpen(true)}
										>
											Move to portal
										</DropdownMenuItem>
									)}
									<DropdownMenuItem Icon={Trash} onClick={handleDelete}>
										Delete
									</DropdownMenuItem>
								</DropdownMenu>
								{canHaveSubpages && (
									<Tooltip label="Add subpage">
										<IconButton
											variant="ghost"
											size="md"
											className="relative hover:bg-gray-200 dark:hover:bg-gray-700"
											type="button"
											Icon={Plus}
											iconStrokeWidth={1.75}
											onClick={handleAddSubpage}
										/>
									</Tooltip>
								)}
							</NavRowActions>
						</RoleCheck>
					</div>
					{hasSubpages && renderSubpages && (
						<div
							inert={!shouldShowSubpages}
							className={cn(
								"ease-[cubic-bezier(0.23,1,0.32,1)] grid transition-[grid-template-rows,opacity] duration-150",
								shouldShowSubpages
									? "grid-rows-[1fr] opacity-100"
									: "grid-rows-[0fr] opacity-40",
							)}
						>
							<div className="min-h-0 overflow-hidden">
								<ul className="ml-[18px] mt-0.5 list-none space-y-0.5 border-l border-gray-200 pl-1 dark:border-gray-700/80">
									<SortableContext items={subpageIds} strategy={noSortShift}>
										{subpages.map((subpage) => (
											<SubpageNavItem
												key={subpage.id}
												subpage={subpage}
												parentArticleId={article.id}
												groupId={groupId}
												sortableDisabled={
													sortableDisabled || !shouldShowSubpages
												}
												isSelected={params.id === subpage.id}
												onSelect={() => {
													void navigate({
														to: "/edit-docs/$id",
														params: { id: subpage.id },
													})
													onItemSelected?.()
												}}
											/>
										))}
									</SortableContext>
								</ul>
							</div>
						</div>
					)}
				</li>
			</>
		)
	}),
)

const SubpageNavItem = memo(
	({
		subpage,
		parentArticleId,
		groupId,
		sortableDisabled,
		isSelected,
		onSelect,
	}: {
		subpage: SubpageWithTranslations
		parentArticleId: string
		groupId: string
		sortableDisabled: boolean
		isSelected: boolean
		onSelect: () => void
	}) => {
		const { selectedLanguage } = useHelpCenterNav()
		const title = translatedTitle(subpage, selectedLanguage)
		const label = getArticleDisplayTitle(subpage, title)

		const sortableData = useMemo(
			() => ({
				type: "article" as const,
				groupId,
				parentArticleId,
				article: subpage,
			}),
			[groupId, parentArticleId, subpage],
		)

		const {
			attributes,
			listeners,
			setNodeRef,
			transform,
			transition,
			isDragging,
		} = useSortable({
			id: subpage.id,
			disabled: sortableDisabled,
			data: sortableData,
		})

		const style = {
			transform: CSS.Translate.toString(transform),
			transition,
			opacity: isDragging ? 0.4 : undefined,
		}
		const dropSide = useDropSignal(subpage.id)

		return (
			<li ref={setNodeRef} style={style} {...attributes}>
				<div
					role="button"
					tabIndex={0}
					className={cn(
						isSelected
							? "bg-gray-100 text-primary dark:bg-gray-800"
							: "bg-transparent text-secondary hover:bg-gray-100 hover:text-primary hover:dark:bg-gray-800/50",
						"group/sublink relative flex h-[1.8rem] w-full min-w-0 cursor-default items-center gap-2 rounded-md pl-2 pr-1 text-[13px] font-medium",
					)}
					{...listeners}
					onClick={onSelect}
					onKeyDown={(e) => {
						if (e.key === "Enter" || e.key === " ") {
							e.preventDefault()
							onSelect()
						}
					}}
				>
					{dropSide === "before" && (
						<div className="pointer-events-none absolute inset-x-1 -top-0.5 z-20 h-0.5 rounded-full bg-black dark:bg-white" />
					)}
					{dropSide === "after" && (
						<div className="pointer-events-none absolute inset-x-1 -bottom-0.5 z-20 h-0.5 rounded-full bg-black dark:bg-white" />
					)}
					{subpage.icon && (
						<NucleoFillIcon
							icon={subpage.icon}
							className="size-3.5 shrink-0 text-secondary opacity-90 group-hover/sublink:text-primary"
						/>
					)}
					<span
						title={label}
						className="min-w-0 flex-1 truncate text-ellipsis text-start opacity-90"
					>
						{label}
					</span>
					<ArticleStatePill article={subpage} className="flex-shrink-0" />
				</div>
			</li>
		)
	},
)
SubpageNavItem.displayName = "SubpageNavItem"
